import { useState, useEffect, useCallback, useRef } from 'react';
import { useApiCache } from '@/lib/contexts/api-cache-context';

/**
 * Hook pour récupérer des données API avec mise en cache automatique
 *
 * @template T
 * @param {string} cacheKey - Clé unique pour le cache (ex: 'user', 'company:123')
 * @param {function(): Promise<T>} fetcher - Fonction async qui retourne les données
 * @param {Object} [options]
 * @param {string} [options.category='default'] - Catégorie TTL ('user', 'company', 'folders', etc.)
 * @param {boolean} [options.enabled=true] - Activer/désactiver le fetch
 * @param {unknown} [options.initialData] - Données initiales
 * @param {function(T): T} [options.transform] - Transformer les données après fetch
 * @param {Array} [options.dependencies=[]] - Dépendances qui déclenchent un refetch
 *
 * @returns {{
 *   data: T | null,
 *   isLoading: boolean,
 *   error: Error | null,
 *   refetch: (force?: boolean) => Promise<void>,
 *   invalidate: () => void
 * }}
 */
export function useCachedApi(cacheKey, fetcher, options = {}) {
    const { category = 'default', enabled = true, initialData = null, transform, dependencies = [] } = options;

    const { getCache, setCache, invalidateCache } = useApiCache();

    const [data, setData] = useState(() => {
        // Essayer de récupérer du cache au montage
        const cached = getCache(cacheKey, category);
        return cached ?? initialData;
    });
    const [isLoading, setIsLoading] = useState(!getCache(cacheKey, category) && enabled);
    const [error, setError] = useState(null);

    const isMountedRef = useRef(true);
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    const fetchData = useCallback(
        async (forceRefresh = false) => {
            if (!enabled) return;

            // Vérifier le cache sauf si forceRefresh
            if (!forceRefresh) {
                const cached = getCache(cacheKey, category);
                if (cached !== null) {
                    setData(transform ? transform(cached) : cached);
                    setIsLoading(false);
                    return;
                }
            }

            setIsLoading(true);
            setError(null);

            try {
                const result = await fetcherRef.current();
                const transformedData = transform ? transform(result) : result;

                if (isMountedRef.current) {
                    setCache(cacheKey, result, category);
                    setData(transformedData);
                    setIsLoading(false);
                }
            } catch (err) {
                if (isMountedRef.current) {
                    setError(err);
                    setIsLoading(false);
                }
            }
        },
        [cacheKey, category, enabled, getCache, setCache, transform],
    );

    // Fetch initial
    useEffect(() => {
        isMountedRef.current = true;
        fetchData();

        return () => {
            isMountedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cacheKey, enabled, ...dependencies]);

    const refetch = useCallback(
        (force = true) => {
            return fetchData(force);
        },
        [fetchData],
    );

    const invalidate = useCallback(() => {
        invalidateCache(cacheKey);
    }, [cacheKey, invalidateCache]);

    return {
        data,
        isLoading,
        error,
        refetch,
        invalidate,
    };
}

/**
 * Hook pour effectuer des mutations avec invalidation automatique du cache
 *
 * @template TData, TPayload
 * @param {function(TPayload): Promise<TData>} mutationFn - Fonction de mutation
 * @param {Object} [options]
 * @param {string|string[]} [options.invalidateKeys] - Clés de cache à invalider après succès
 * @param {function(TData): void} [options.onSuccess] - Callback après succès
 * @param {function(Error): void} [options.onError] - Callback après erreur
 *
 * @returns {{
 *   mutate: (payload: TPayload) => Promise<TData>,
 *   isLoading: boolean,
 *   error: Error | null,
 *   reset: () => void
 * }}
 */
export function useCachedMutation(mutationFn, options = {}) {
    const { invalidateKeys, onSuccess, onError } = options;
    const { invalidateCache } = useApiCache();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const mutationFnRef = useRef(mutationFn);
    mutationFnRef.current = mutationFn;

    const mutate = useCallback(
        async (payload) => {
            setIsLoading(true);
            setError(null);

            try {
                const result = await mutationFnRef.current(payload);

                // Invalider les clés de cache spécifiées
                if (invalidateKeys) {
                    const keys = Array.isArray(invalidateKeys) ? invalidateKeys : [invalidateKeys];
                    keys.forEach((key) => invalidateCache(key));
                }

                onSuccess?.(result);
                setIsLoading(false);
                return result;
            } catch (err) {
                setError(err);
                setIsLoading(false);
                onError?.(err);
                throw err;
            }
        },
        [invalidateCache, invalidateKeys, onSuccess, onError],
    );

    const reset = useCallback(() => {
        setError(null);
        setIsLoading(false);
    }, []);

    return {
        mutate,
        isLoading,
        error,
        reset,
    };
}

/**
 * Déclencher un événement de refresh global (utile pour pull-to-refresh)
 */
export function triggerGlobalRefresh() {
    window.dispatchEvent(new CustomEvent('horodaty:refresh'));
}
